import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { speichereBuchung, loescheBuchung } from 'modules/finanzen/features/finanzenSlice';
import { selectInvestitionen } from 'modules/investitionen/features/investitionenSlice';
import { heute } from 'modules/finanzen/features/berechnung';
import { TYP_AUSGABE, TYP_EINNAHME, ZAHLWEGE, STANDARD_ZAHLWEG } from 'modules/finanzen/features/konstanten';
import { letzterOrt, merkeOrt } from 'modules/finanzen/features/ort';
import KategorieAuswahl from 'modules/finanzen/components/KategorieAuswahl';
import OrtAuswahl from 'modules/finanzen/components/OrtAuswahl';

const inputKlasse =
  'w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none';

/** Ein auswaehlbarer Chip (Kategorie oder Zahlweg). */
function Chip({ aktiv, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
        aktiv
          ? 'border-brand-600 bg-brand-600 text-white'
          : 'border-line bg-paper text-ink hover:border-brand-400'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Modaler Schnell-Erfassen-Editor fuer eine Buchung: Typ-Umschalter, grosser
 * Betrag, Kategorie- und Zahlweg-Chips, Datum und Notiz. Speichert verschluesselt
 * ueber den Slice. Vorbelegt auf Ausgabe + Zahlweg "Handy" fuer wenige Taps.
 */
export default function BuchungForm({ buchung, onClose }) {
  const dispatch = useDispatch();
  const orte = useSelector(selectInvestitionen);
  const [typ, setTyp] = useState(buchung.typ || TYP_AUSGABE);
  const [betrag, setBetrag] = useState(buchung.betrag != null ? String(buchung.betrag) : '');
  const [kategorie, setKategorie] = useState(buchung.kategorie || '');
  const [zahlweg, setZahlweg] = useState(buchung.zahlweg || STANDARD_ZAHLWEG);
  const [ort, setOrt] = useState(buchung.ort || (buchung.uuid ? '' : letzterOrt() || ''));
  const [datum, setDatum] = useState(buchung.datum || heute());
  const [notiz, setNotiz] = useState(buchung.notiz || '');
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);

  const istNeu = !buchung.uuid;

  // Beim Typ-Wechsel die Kategorie zuruecksetzen (Ausgabe/Einnahme haben eigene Listen).
  const wechsleTyp = (neuerTyp) => {
    setTyp(neuerTyp);
    setKategorie('');
  };

  const absenden = async (e) => {
    e.preventDefault();
    const betragZahl = Number(String(betrag).replace(',', '.'));
    if (!betragZahl || betragZahl <= 0) {
      setFehler('Bitte einen Betrag größer 0 angeben.');
      return;
    }
    if (!kategorie) {
      setFehler('Bitte eine Kategorie wählen.');
      return;
    }
    if (orte.length > 0 && !ort) {
      setFehler('Bitte einen Investitionsort wählen.');
      return;
    }
    setFehler(null);
    setLaeuft(true);
    try {
      await dispatch(
        speichereBuchung({
          uuid: buchung.uuid,
          sortierung: buchung.sortierung,
          typ,
          betrag: betragZahl,
          kategorie,
          zahlweg,
          ort: ort || null,
          datum,
          notiz: notiz.trim(),
        })
      ).unwrap();
      merkeOrt(ort);
      onClose();
    } catch (ex) {
      setFehler('Speichern fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setLaeuft(false);
    }
  };

  const entfernen = async () => {
    if (!window.confirm('Diese Buchung wirklich löschen?')) return;
    setLaeuft(true);
    try {
      await dispatch(loescheBuchung(buchung.uuid)).unwrap();
      onClose();
    } catch (ex) {
      setFehler('Löschen fehlgeschlagen.');
      setLaeuft(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-paper p-6 shadow-xl">
        <h2 className="mb-4 font-serif text-xl font-semibold text-ink">
          {istNeu ? 'Buchung erfassen' : 'Buchung bearbeiten'}
        </h2>

        <form onSubmit={absenden} className="space-y-4">
          {/* Typ-Umschalter */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => wechsleTyp(TYP_AUSGABE)}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                typ === TYP_AUSGABE ? 'border-loss bg-loss/10 text-loss' : 'border-line text-ink-muted'
              }`}
            >
              − Ausgabe
            </button>
            <button
              type="button"
              onClick={() => wechsleTyp(TYP_EINNAHME)}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                typ === TYP_EINNAHME ? 'border-gain bg-gain/10 text-gain' : 'border-line text-ink-muted'
              }`}
            >
              + Einnahme
            </button>
          </div>

          {/* Betrag (gross, im Fokus) */}
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Betrag</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={betrag}
                onChange={(e) => setBetrag(e.target.value)}
                placeholder="0,00"
                className="w-full rounded-lg border border-line bg-paper px-3 py-3 text-right font-serif text-3xl font-semibold text-ink focus:border-brand-600 focus:outline-none tabular-nums"
                autoFocus
              />
              <span className="font-serif text-2xl text-ink-muted">€</span>
            </div>
          </div>

          {/* Kategorie-Auswahl (Standard + eigene, inkl. Anlegen) */}
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Kategorie</label>
            <KategorieAuswahl typ={typ} wert={kategorie} onChange={setKategorie} />
          </div>

          {/* Zahlweg-Chips */}
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Zahlweg</label>
            <div className="flex flex-wrap gap-2">
              {ZAHLWEGE.map((z) => (
                <Chip key={z.name} aktiv={zahlweg === z.name} onClick={() => setZahlweg(z.name)}>
                  {z.icon} {z.name}
                </Chip>
              ))}
            </div>
          </div>

          {/* Investitionsort – bucht auf dessen Barvermoegen */}
          <div>
            <label className="mb-1 block text-sm font-medium text-ink">Investitionsort</label>
            <OrtAuswahl wert={ort} onChange={setOrt} />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium text-ink">Datum</label>
              <input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className={inputKlasse} />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium text-ink">Notiz (optional)</label>
              <input
                value={notiz}
                onChange={(e) => setNotiz(e.target.value)}
                placeholder="z. B. Rewe"
                className={inputKlasse}
              />
            </div>
          </div>

          {fehler && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}

          <div className="flex items-center justify-between gap-3 pt-1">
            {!istNeu ? (
              <button
                type="button"
                onClick={entfernen}
                disabled={laeuft}
                className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 disabled:opacity-50"
              >
                Löschen
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={laeuft}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
              >
                {laeuft ? 'Speichere…' : 'Speichern'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
